import React, { useEffect, useMemo, useState } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import "./App.css";
import Navbar from "./components/Navbar";
import HeroCover from "./components/HeroCover";
import BestSellers from "./components/BestSellers";
import OurStory from "./components/OurStory";
import Products from "./components/Products";
import Community from "./components/Community";
import ContactUs from "./components/ContactUs";
import Footer from "./components/Footer";
import * as gtag from "./gtag";
import PromoModal from "./components/PromoModal";
import api from "./api";
import CartDrawer from "./components/CartDrawer";
import FloatingActions from "./components/FloatingActions";
import { useCart } from "./context/CartContext";
import { ShoppingBag } from "lucide-react";
import { useReveal } from "./hooks/useReveal";

const OPT_OUT_KEY = "promoModalOptOut";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function Home() {
  const query = useQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const { totalItems } = useCart();

  // Fetch site config once (topbar text + promo modal content)
  useEffect(() => {
    api
      .get("/config")
      .then((res) => setConfig(res.data?.data ?? res.data))
      .catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    const optedOut = localStorage.getItem(OPT_OUT_KEY) === "true";
    const hasQrFlag = query.get("utm_campaign") === "pisu";
    const promoActive = config?.promo?.active ?? false;

    if (!optedOut && hasQrFlag && promoActive) {
      setIsModalOpen(true);
    }
  }, [query, config]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src") || params.get("utm_source");
    if (src) {
      window.gtag && window.gtag("event", "qr_scan", { qr_source: src });
    }
  }, []);

  const handleClose = () => setIsModalOpen(false);
  const handleOptOut = () => {
    try { localStorage.setItem(OPT_OUT_KEY, "true"); } catch {}
    setIsModalOpen(false);
  };

  const refBestSellers = useReveal();
  const refEvents      = useReveal();
  const refProducts    = useReveal();
  const refCommunity   = useReveal();
  const refContact     = useReveal();

  return (
    <div className="App">
      <Navbar topbarText={config?.topbarText ?? null} />

      <FloatingActions />

      {/* Floating cart button — visible once items are added */}
      {totalItems > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          aria-label="Open cart"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-primary text-white rounded-full px-4 py-3 shadow-lg hover:bg-primary/90 transition-all"
        >
          <ShoppingBag size={20} />
          <span className="text-sm font-semibold">{totalItems}</span>
        </button>
      )}

      <HeroCover />
      <div ref={refBestSellers} className="reveal"><BestSellers /></div>
      <div ref={refEvents}      className="reveal"><OurStory /></div>
      <div ref={refProducts}    className="reveal"><Products /></div>
      <div ref={refCommunity}   className="reveal"><Community /></div>
      <div ref={refContact}     className="reveal"><ContactUs /></div>
      <Footer />

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      <PromoModal
        isOpen={isModalOpen}
        onClose={handleClose}
        onOptOut={handleOptOut}
        promo={config?.promo ?? null}
      />
    </div>
  );
}

function App() {
  const location = useLocation();

  useEffect(() => {
    const url = location.pathname + location.search;
    gtag.pageview(url);
  }, [location]);

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
